import type { EventSettings, GlobalSettings, Schemas } from "../api/client";

// Passages subject to the crew's confirmation (chantier Confirmations §1.2, 1.2).
export type PassageKind = Schemas["PassageKind"];
/** 1.3.0: crossing a course off its crossing points is always subject to confirmation, not a setting */
export type ConfigurableKind = Exclude<PassageKind, "OFF_POINT_CROSSING">;
export type ConfirmationFlags = Schemas["ConfirmationFlags"];
export type FlagKey = keyof ConfirmationFlags;
export type ConfirmationMode = "normal" | "emergency";

export const MODES: readonly ConfirmationMode[] = ["normal", "emergency"];

/** Every kind, in the order of the enumeration. */
export const ALL_PASSAGE_KINDS: readonly PassageKind[] = [
  "BARRIER_CROSSING", "EMERGENCY_LANE", "COURSE_WITH_FLOW", "COURSE_CROSSING", "COURSE_AGAINST_FLOW", "WRONG_WAY", "BUS_LANE", "PEDESTRIAN_ZONE",
  "CROSSING_POINT", "OFF_POINT_CROSSING",
];

/** Kinds of the settings, in the order of the enumeration, as AppliedSettings.confirmations. */
export const PASSAGE_KINDS: readonly ConfigurableKind[] = ALL_PASSAGE_KINDS.filter((k): k is ConfigurableKind => k !== "OFF_POINT_CROSSING");

/** Kinds without a normal-mode setting. */
export const EMERGENCY_ONLY_KINDS: readonly PassageKind[] = ["COURSE_AGAINST_FLOW", "BUS_LANE", "PEDESTRIAN_ZONE"];

export const FLAG_KEY: Record<ConfigurableKind, FlagKey> = {
  BARRIER_CROSSING: "barrierCrossing", EMERGENCY_LANE: "emergencyLane", COURSE_WITH_FLOW: "courseWithFlow", COURSE_CROSSING: "courseCrossing",
  COURSE_AGAINST_FLOW: "courseAgainstFlow", WRONG_WAY: "wrongWay", BUS_LANE: "busLane", PEDESTRIAN_ZONE: "pedestrianZone",
  CROSSING_POINT: "crossingPoint",
};

export function appliesTo(kind: PassageKind, mode: ConfirmationMode): boolean {
  return mode === "emergency" || !EMERGENCY_ONLY_KINDS.includes(kind);
}

const NONE: ConfirmationFlags = {
  barrierCrossing: false, emergencyLane: false, courseWithFlow: false, courseCrossing: false,
  courseAgainstFlow: false, wrongWay: false, busLane: false, pedestrianZone: false, crossingPoint: false,
};

/** Engine defaults, used when a global value is absent (server older than 1.2). */
export const CONFIRMATION_DEFAULTS = {
  confirmations: { normal: NONE, emergency: { ...NONE, courseAgainstFlow: true } },
  confirmationMinGainS: 30,
  reofferGainAbsoluteS: 60,
  reofferGainRelative: 0.5,
} as const;
export type ConfirmationNumberKey = "confirmationMinGainS" | "reofferGainAbsoluteS" | "reofferGainRelative";
export const CONFIRMATION_NUMBER_KEYS: readonly ConfirmationNumberKey[] = ["confirmationMinGainS", "reofferGainAbsoluteS", "reofferGainRelative"];

/** Bounds of the server: minimal gain 0 to 15 min, re-offer 0 to 30 min and 0 to 300 %. */
export const CONFIRMATION_MIN_GAIN_MIN_MAX = 15;
export const REOFFER_ABS_MIN_MAX = 30;
export const REOFFER_REL_PCT_MAX = 300;

/** Global flags of both modes: a missing mode takes the default, a missing flag of a given mode is false. */
export function globalConfirmations(s: GlobalSettings | null | undefined): Record<ConfirmationMode, ConfirmationFlags> {
  const c = s?.confirmations;
  return {
    normal: c?.normal ? { ...NONE, ...c.normal } : CONFIRMATION_DEFAULTS.confirmations.normal,
    emergency: c?.emergency ? { ...NONE, ...c.emergency } : CONFIRMATION_DEFAULTS.confirmations.emergency,
  };
}

export function globalConfirmationNumber(s: GlobalSettings | null | undefined, k: ConfirmationNumberKey): number {
  return s?.[k] ?? CONFIRMATION_DEFAULTS[k];
}

/** Event override of one cell: undefined means the global value. */
export function overrideOf(s: EventSettings, mode: ConfirmationMode, kind: ConfigurableKind): boolean | undefined {
  return s.confirmations?.[mode]?.[FLAG_KEY[kind]] ?? undefined;
}

/** Settings with one cell overridden (`undefined`: back to the global value), empty objects dropped. */
export function withOverride(s: EventSettings, mode: ConfirmationMode, kind: ConfigurableKind, v: boolean | undefined): EventSettings {
  const modes: Record<string, Record<string, boolean>> = { ...(s.confirmations ?? {}) } as Record<string, Record<string, boolean>>;
  const o = { ...(modes[mode] ?? {}) };
  if (v === undefined) delete o[FLAG_KEY[kind]]; else o[FLAG_KEY[kind]] = v;
  if (Object.keys(o).length) modes[mode] = o; else delete modes[mode];
  return { ...s, confirmations: Object.keys(modes).length ? modes : undefined };
}

/** Number of confirmation settings forced by the event (cells of the emergency mode only are not counted in normal mode). */
export function confirmationOverrideCount(s: EventSettings): number {
  let n = 0;
  for (const mode of MODES) for (const k of PASSAGE_KINDS) if (appliesTo(k, mode) && overrideOf(s, mode, k) != null) n++;
  return n + CONFIRMATION_NUMBER_KEYS.filter((k) => s[k] != null).length;
}

/**
 * Kinds of the `ap` URL parameter of the route test; COURSE_AGAINST_FLOW is authorised per course (`ac`), CROSSING_POINT
 * per point (`acp`, 1.3.0).
 */
export function parsePassageKinds(v: string | null): PassageKind[] {
  const all = (v ?? "").split(",").map((x) => x.trim()).filter((x): x is PassageKind =>
    (ALL_PASSAGE_KINDS as readonly string[]).includes(x) && x !== "COURSE_AGAINST_FLOW" && x !== "CROSSING_POINT");
  return [...new Set(all)];
}

/** Kinds kept when going back to normal mode. */
export function normalModeKinds(kinds: readonly PassageKind[]): PassageKind[] {
  return kinds.filter((k) => appliesTo(k, "normal"));
}

/** Kinds confirmed in one mode according to the applied settings of a route response, and whether the event forces one of them. */
export function confirmedKinds(applied: readonly Schemas["AppliedConfirmation"][] | undefined, mode: ConfirmationMode): { kinds: PassageKind[]; fromEvent: boolean } {
  const cells = (applied ?? []).map((c) => ({ kind: c.kind, v: c[mode] })).filter((c) => c.v != null);
  return { kinds: cells.filter((c) => c.v!.value).map((c) => c.kind), fromEvent: cells.some((c) => c.v!.source === "EVENT") };
}
